import { useEffect, useRef } from 'react';

/**
 * What the full-screen print previews share — the sub sheets and the
 * close-out report, each a portal on <body> printed with the browser's dialog.
 *
 * Focus moves to the Close button once, on open, and Escape closes unless
 * busy. Once: re-running on every render (onClose is a new function each time
 * the tab re-renders) pulled focus back to Close after every refresh (review).
 * `mounted` lets a print still waiting on its photos stand down when the
 * preview was closed meanwhile (Codex on #567).
 */
export function usePrintPreview(onClose: () => void, busy: boolean) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const mounted = useRef(true);
  const latest = useRef({ onClose, busy });
  useEffect(() => { latest.current = { onClose, busy }; });
  useEffect(() => {
    mounted.current = true;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !latest.current.busy) latest.current.onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { mounted.current = false; window.removeEventListener('keydown', onKey); };
  }, []);
  return { closeRef, mounted };
}

/**
 * Before printing: wait for the preview's photos, then, if any is missing,
 * ask before printing without it. A printed sheet or report is a record, so
 * empty photo boxes mustn't happen by accident (Codex on #567). Counted:
 * photos with no preview link (signed out, or SharePoint refused or lost the
 * file), and previews the browser failed to load (an expired link, the
 * network) — a failed image is "complete" with no pixels.
 *
 * False: don't print — the preview closed meanwhile, or the user said no.
 */
export async function readyToPrint(
  root: HTMLElement | null,
  photoUrls: string[],
  thumbs: Record<string, string>,
  needsSignIn: boolean,
  mounted: { current: boolean },
): Promise<boolean> {
  const imgs = [...(root?.querySelectorAll<HTMLImageElement>('img.pl-sheet-photo') ?? [])];
  await Promise.all(imgs.map((img) => (img.complete ? null : img.decode().catch(() => null))));
  if (!mounted.current) return false;
  const noPhoto = photoUrls.filter((u) => !thumbs[u]).length + imgs.filter((img) => img.naturalWidth === 0).length;
  if (noPhoto === 0) return true;
  return window.confirm(needsSignIn
    ? 'The photos aren’t showing — use “Show photos” to sign in to SharePoint. Print without them?'
    : `${noPhoto === 1 ? 'A photo' : `${noPhoto} photos`} couldn’t be loaded from SharePoint. Print without ${noPhoto === 1 ? 'it' : 'them'}?`);
}
